import { useMemo } from 'react';
import { CouponDuration } from '../../../../enums/coupon-duration';

export const currentBillingPeriodFilter = (coupon) => coupon.duration === CouponDuration.Forever || coupon.isAppliedToInvoice;

const FULLY_DISCOUNTED_PLAN_PRICE = 0;

/**
 * Applies combined coupon discounts to a cost
 * @param {number} cost - The cost value to discount
 * @param {Array} coupons - Array of coupon objects with discountPercent property
 * @returns {number} The discounted cost
 */
const applyCouponDiscount = (cost, coupons) => {
  const totalDiscountPercent = coupons.reduce((sum, coupon) => sum + (coupon.discountPercent ?? 0), 0);

  if (totalDiscountPercent >= 100) {
    return FULLY_DISCOUNTED_PLAN_PRICE;
  }

  return cost * (1 - totalDiscountPercent / 100);
};

/**
 * Gets the coupon-adjusted cost based on a filter function
 * @param {number} cost - The cost to adjust
 * @param {Array} coupons - Array of coupon objects
 * @param {(coupon: object) => boolean} filterFunction - Filter function to apply to coupons
 * @returns {number} The adjusted cost
 */
const getCouponAdjustedCostFromFilter = (cost, coupons, filterFunction) => {
  if (coupons.length === 0) {
    return cost;
  }

  const filteredCoupons = coupons.filter(filterFunction);

  if (filteredCoupons.length === 0) {
    return cost;
  }

  return applyCouponDiscount(cost, filteredCoupons);
};

export const useGetCouponAdjustedTotalCost = (coupons, totalCostValue, totalCostCode) => {
  return useMemo(
    () => ({
      currentBillingPeriodCost: {
        code: totalCostCode,
        value: getCouponAdjustedCostFromFilter(totalCostValue, coupons, currentBillingPeriodFilter),
      },
    }),
    [totalCostValue, totalCostCode, coupons],
  );
};
